/* eslint-disable react/forbid-prop-types,react-hooks/exhaustive-deps */
import { CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import LocalizedText from 'commons/LocalizedText/LocalizedText';
import useMobile from 'hook/UseMobile';
import useQueryState from 'hook/UseQueryState';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { ParamTypes } from 'utils/constants/param.type';
import { MOBILE_BREAKPOINT } from 'utils/constants/responsive';

export const MIN_ROWS_FOR_PAGINATION = 5;
export const INITIAL_PAGE = 0;
export const INITIAL_ROWS_PER_PAGE = 20;

const CELL_STYLE = { maxWidth: '30vw', overflowX: 'overlay', overflowY: 'hidden' };

const useStyles = makeStyles((theme) => ({
  white: {
    backgroundColor: theme.palette.background.paper
  },
  table: {
    marginBottom: '20px'
  },
  rowStriped: {
    '&:nth-child(even)': {
      backgroundColor: theme.palette.primary.light
    },
    '& > td': {
      [theme.breakpoints.down('sm')]: {
        padding: '2px 10px 2px 10px'
      }
    }
  },
  hoverRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.primary.tableHover
    }
  },
  tableHeader: {
    ...CELL_STYLE,
    borderBottom: '1px solid black',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    [theme.breakpoints.down('sm')]: {
      padding: '3px 12px 3px 12px'
    },
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
      padding: '1px 8px 1px 8px'
    }
  },
  tableCell: {
    ...CELL_STYLE
  },
  overflowHidden: {
    overflow: 'hidden'
  }
}));

const desc = (a, b, orderBy, sortOnProperty) => {
  if (b[orderBy] && b[orderBy][sortOnProperty]) {
    if (b[orderBy][sortOnProperty] < a[orderBy][sortOnProperty]) return -1;
    if (b[orderBy][sortOnProperty] > a[orderBy][sortOnProperty]) return 1;
  }else{
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
  }
  return 0;
};

export const CustomTableWithExternalPagination = ({
  rows, headers, onRowCLick, stickyHeader, filters, isLoading, onChange, isBackend, page, rowsPerPage, initialOrderBy, setPage
}) => {
  const classes = useStyles();
  const isMobile = useMobile();
  const [order, setOrder] = useQueryState('order', 'asc', ParamTypes.STRING);
  const [orderBy, setOrderBy] = useQueryState('orderBy', headers.orderBy || initialOrderBy || '', ParamTypes.STRING);
  const [sortOnProperty, setSortOnProperty] = useState(headers.sortOn || 'name');
  const [finalRows, setFinalRows] = useState(rows);

  const handleSort = ({ name, sortOn }) => {
    setPage(0);
    const isDesc = orderBy === name && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(name);
    setSortOnProperty(sortOn);
    onChange();
  };

  const getSorting = () => (order === 'desc'
    ? (a, b) => desc(a, b, orderBy, sortOnProperty)
    : (a, b) => -desc(a, b, orderBy, sortOnProperty));

  useEffect(() => {
    setFinalRows(isBackend
      ? rows
      : rows.sort(getSorting()).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage))
  }, [order, orderBy, sortOnProperty, rows, rowsPerPage, page])

  const finalHeaders = isMobile
    ? headers.filter((header) => header.isOnMobile)
    : headers;

  return (
    <>
      <Table
        className={`${classes.table} ${classes.white}`}
        size="small"
        stickyHeader={stickyHeader}
      >
        <TableHead>
          <TableRow>
            {finalHeaders.map((header, index) => (
              <TableCell
                className={`${classes.white} ${classes.tableHeader}`}
                key={`${header.name}${index}`}
                style={isMobile ? {minWidth : header.mobileWidth} : {minWidth : header.width}}
              >
                {header.isSortable ? (
                  <TableSortLabel
                    active={orderBy === header.name}
                    direction={order}
                    onClick={() => handleSort({ ...header })}
                  >
                    <LocalizedText text={header.label} />
                  </TableSortLabel>
                )
                  : <LocalizedText text={header.label} />}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {finalRows.length === 0
            ? (
              !isLoading &&
              <TableRow>
                <TableCell align='center' colSpan={finalHeaders.length}>
                  <LocalizedText align="center" variant="h6">noDataInTable</LocalizedText>
                </TableCell>
              </TableRow>
            )
            : finalRows.map((row, index) => (
              <TableRow
                className={`${classes.rowStriped} ${onRowCLick ? classes.hoverRow : ''}`}
                key={`${row.id}${index}`}
                onClick={() => onRowCLick && onRowCLick(row)}
              >
                {finalHeaders.map((column) => (
                  <TableCell
                    className={classes.tableCell}
                    key={`${column.name}${index}`}
                    style={isMobile ? {minWidth : column.mobileWidth} : {minWidth : column.width}}
                  >
                    {column.template(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
        </TableBody>
        {isLoading &&
          <TableRow>
            <TableCell align='center' colSpan={finalHeaders.length}>
              <CircularProgress />
            </TableCell>
          </TableRow>
        }
      </Table>
    </>
  );
};

CustomTableWithExternalPagination.propTypes = {
  filters: PropTypes.object,
  headers: PropTypes.arrayOf(PropTypes.shape({
    isOnMobile: PropTypes.bool,
    isSortable: PropTypes.bool,
    label: PropTypes.string,
    name: PropTypes.string,
    sortOn: PropTypes.string,
    template: PropTypes.func
  })).isRequired,
  isBackend: PropTypes.bool,
  isLoading: PropTypes.bool,
  onChange: PropTypes.func,
  onRowCLick: PropTypes.func,
  rowKey: PropTypes.string,
  rows: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  stickyHeader: PropTypes.bool,
  total: PropTypes.number
};

CustomTableWithExternalPagination.defaultProps = {
  filters: {},
  isBackend: false,
  isLoading: false,
  onChange: () => {},
  onRowCLick: null,
  rowKey: null,
  stickyHeader: false,
  total: null
};